import { useState, useMemo, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';


/**
 * Curated emoji catalogue organised by finance-relevant categories.
 * Each entry is { emoji, label } so we can filter on the label text.
 */
const EMOJI_CATEGORIES: { name: string; icon: string; emojis: { emoji: string; label: string }[] }[] = [
  {
    name: 'Money & Finance',
    icon: '💰',
    emojis: [
      { emoji: '💰', label: 'Money bag' },
      { emoji: '💵', label: 'Dollar bill' },
      { emoji: '💴', label: 'Yen bill' },
      { emoji: '💶', label: 'Euro bill' },
      { emoji: '💷', label: 'Pound bill' },
      { emoji: '💳', label: 'Credit card' },
      { emoji: '🏦', label: 'Bank' },
      { emoji: '💸', label: 'Money wings' },
      { emoji: '🪙', label: 'Coin' },
      { emoji: '💎', label: 'Gem' },
      { emoji: '🏧', label: 'ATM' },
      { emoji: '💲', label: 'Dollar sign' },
      { emoji: '🧾', label: 'Receipt' },
      { emoji: '💱', label: 'Currency exchange' },
      { emoji: '📑', label: 'Invoice' },
      { emoji: '🏛️', label: 'Classical building' },
      { emoji: '🤑', label: 'Money face' },
      { emoji: '💹', label: 'Chart up yen' },
    ],
  },
  {
    name: 'Shopping & Retail',
    icon: '🛒',
    emojis: [
      { emoji: '🛒', label: 'Shopping cart' },
      { emoji: '🛍️', label: 'Shopping bags' },
      { emoji: '🏪', label: 'Convenience store' },
      { emoji: '🏬', label: 'Department store' },
      { emoji: '🎁', label: 'Gift' },
      { emoji: '🧥', label: 'Coat' },
      { emoji: '👟', label: 'Shoes' },
      { emoji: '👗', label: 'Dress' },
      { emoji: '👜', label: 'Handbag' },
      { emoji: '🕶️', label: 'Sunglasses' },
      { emoji: '⌚', label: 'Watch' },
      { emoji: '🧴', label: 'Lotion' },
      { emoji: '💄', label: 'Lipstick' },
      { emoji: '🪥', label: 'Toothbrush' },
      { emoji: '🧸', label: 'Teddy bear' },
    ],
  },
  {
    name: 'Food & Drink',
    icon: '🍕',
    emojis: [
      { emoji: '🍕', label: 'Pizza' },
      { emoji: '🍔', label: 'Burger' },
      { emoji: '☕', label: 'Coffee' },
      { emoji: '🍺', label: 'Beer' },
      { emoji: '🍷', label: 'Wine' },
      { emoji: '🥗', label: 'Salad' },
      { emoji: '🍣', label: 'Sushi' },
      { emoji: '🍲', label: 'Stew' },
      { emoji: '🥐', label: 'Croissant' },
      { emoji: '🧁', label: 'Cupcake' },
      { emoji: '🍜', label: 'Noodles' },
      { emoji: '🍱', label: 'Bento box' },
      { emoji: '🥘', label: 'Pot of food' },
      { emoji: '🧇', label: 'Waffle' },
      { emoji: '🍰', label: 'Cake' },
      { emoji: '🍩', label: 'Donut' },
      { emoji: '🫖', label: 'Teapot' },
      { emoji: '🥤', label: 'Cup with straw' },
      { emoji: '🍦', label: 'Ice cream' },
      { emoji: '🥡', label: 'Takeout box' },
      { emoji: '🍳', label: 'Cooking' },
      { emoji: '🥩', label: 'Meat' },
      { emoji: '🥑', label: 'Avocado' },
      { emoji: '🫘', label: 'Beans' },
      { emoji: '🍚', label: 'Rice' },
    ],
  },
  {
    name: 'Transport & Travel',
    icon: '🚗',
    emojis: [
      { emoji: '🚗', label: 'Car' },
      { emoji: '✈️', label: 'Airplane' },
      { emoji: '🚌', label: 'Bus' },
      { emoji: '🚇', label: 'Metro' },
      { emoji: '⛽', label: 'Fuel' },
      { emoji: '🚕', label: 'Taxi' },
      { emoji: '🚲', label: 'Bicycle' },
      { emoji: '🏍️', label: 'Motorcycle' },
      { emoji: '🚂', label: 'Train' },
      { emoji: '🚢', label: 'Ship' },
      { emoji: '🛳️', label: 'Cruise ship' },
      { emoji: '🗺️', label: 'World map' },
      { emoji: '🧳', label: 'Luggage' },
      { emoji: '🏨', label: 'Hotel' },
      { emoji: '🚁', label: 'Helicopter' },
      { emoji: '⛴️', label: 'Ferry' },
      { emoji: '🛵', label: 'Motor scooter' },
      { emoji: '🚡', label: 'Aerial tramway' },
      { emoji: '🅿️', label: 'Parking' },
    ],
  },
  {
    name: 'Home & Living',
    icon: '🏠',
    emojis: [
      { emoji: '🏠', label: 'House' },
      { emoji: '🏡', label: 'Home garden' },
      { emoji: '🔑', label: 'Key' },
      { emoji: '🛋️', label: 'Couch' },
      { emoji: '💡', label: 'Light bulb' },
      { emoji: '🔧', label: 'Wrench' },
      { emoji: '🧹', label: 'Broom' },
      { emoji: '🪴', label: 'Plant' },
      { emoji: '🛏️', label: 'Bed' },
      { emoji: '🚿', label: 'Shower' },
      { emoji: '🧲', label: 'Magnet' },
      { emoji: '🔌', label: 'Electric plug' },
      { emoji: '📦', label: 'Package' },
      { emoji: '🪑', label: 'Chair' },
      { emoji: '🧽', label: 'Sponge' },
      { emoji: '🪣', label: 'Bucket' },
      { emoji: '🔨', label: 'Hammer' },
      { emoji: '🪛', label: 'Screwdriver' },
    ],
  },
  {
    name: 'Health & Wellness',
    icon: '❤️',
    emojis: [
      { emoji: '❤️', label: 'Heart' },
      { emoji: '💊', label: 'Pill' },
      { emoji: '🏥', label: 'Hospital' },
      { emoji: '🏋️', label: 'Gym' },
      { emoji: '🧘', label: 'Meditation' },
      { emoji: '🩺', label: 'Stethoscope' },
      { emoji: '🦷', label: 'Tooth' },
      { emoji: '🩹', label: 'Bandage' },
      { emoji: '💉', label: 'Syringe' },
      { emoji: '🏃', label: 'Running' },
      { emoji: '🧬', label: 'DNA' },
      { emoji: '🥦', label: 'Healthy food' },
      { emoji: '🫀', label: 'Heart organ' },
      { emoji: '🧠', label: 'Brain' },
      { emoji: '👁️', label: 'Eye' },
      { emoji: '🩻', label: 'X-ray' },
    ],
  },
  {
    name: 'Education & Learning',
    icon: '📚',
    emojis: [
      { emoji: '📚', label: 'Books' },
      { emoji: '🎓', label: 'Graduation' },
      { emoji: '✏️', label: 'Pencil' },
      { emoji: '📝', label: 'Memo' },
      { emoji: '💻', label: 'Laptop' },
      { emoji: '📖', label: 'Open book' },
      { emoji: '🔬', label: 'Microscope' },
      { emoji: '🔭', label: 'Telescope' },
      { emoji: '📐', label: 'Triangle ruler' },
      { emoji: '🧮', label: 'Abacus' },
      { emoji: '📓', label: 'Notebook' },
      { emoji: '🗒️', label: 'Notepad' },
      { emoji: '🎒', label: 'Backpack' },
      { emoji: '🖊️', label: 'Pen' },
    ],
  },
  {
    name: 'Work & Office',
    icon: '💼',
    emojis: [
      { emoji: '💼', label: 'Briefcase' },
      { emoji: '📊', label: 'Bar chart' },
      { emoji: '📈', label: 'Trending up' },
      { emoji: '📉', label: 'Trending down' },
      { emoji: '🖥️', label: 'Desktop computer' },
      { emoji: '📎', label: 'Paperclip' },
      { emoji: '🖨️', label: 'Printer' },
      { emoji: '📋', label: 'Clipboard' },
      { emoji: '📁', label: 'Folder' },
      { emoji: '🗂️', label: 'Card index' },
      { emoji: '📧', label: 'Email' },
      { emoji: '💬', label: 'Speech bubble' },
      { emoji: '🤝', label: 'Handshake' },
      { emoji: '📆', label: 'Calendar' },
      { emoji: '⏰', label: 'Alarm clock' },
    ],
  },
  {
    name: 'Entertainment',
    icon: '🎬',
    emojis: [
      { emoji: '🎬', label: 'Movie' },
      { emoji: '🎮', label: 'Gaming' },
      { emoji: '🎵', label: 'Music' },
      { emoji: '🎭', label: 'Theater' },
      { emoji: '📺', label: 'Television' },
      { emoji: '🎨', label: 'Art' },
      { emoji: '⚽', label: 'Soccer' },
      { emoji: '🏀', label: 'Basketball' },
      { emoji: '🎾', label: 'Tennis' },
      { emoji: '🎳', label: 'Bowling' },
      { emoji: '🎪', label: 'Circus' },
      { emoji: '🎤', label: 'Microphone' },
      { emoji: '📷', label: 'Camera' },
      { emoji: '🎸', label: 'Guitar' },
      { emoji: '🎹', label: 'Piano' },
      { emoji: '📱', label: 'Phone' },
      { emoji: '🎧', label: 'Headphones' },
      { emoji: '🕹️', label: 'Joystick' },
    ],
  },
  {
    name: 'Family & Kids',
    icon: '👶',
    emojis: [
      { emoji: '👶', label: 'Baby' },
      { emoji: '👨‍👩‍👧', label: 'Family' },
      { emoji: '🍼', label: 'Baby bottle' },
      { emoji: '🧸', label: 'Toy' },
      { emoji: '🎠', label: 'Carousel' },
      { emoji: '🧒', label: 'Child' },
      { emoji: '👪', label: 'Family group' },
      { emoji: '🎈', label: 'Balloon' },
      { emoji: '🧩', label: 'Puzzle' },
      { emoji: '📸', label: 'Photo' },
    ],
  },
  {
    name: 'Pets & Animals',
    icon: '🐾',
    emojis: [
      { emoji: '🐾', label: 'Paw prints' },
      { emoji: '🐕', label: 'Dog' },
      { emoji: '🐈', label: 'Cat' },
      { emoji: '🐠', label: 'Fish' },
      { emoji: '🐦', label: 'Bird' },
      { emoji: '🐹', label: 'Hamster' },
      { emoji: '🐢', label: 'Turtle' },
      { emoji: '🐴', label: 'Horse' },
      { emoji: '🦮', label: 'Guide dog' },
    ],
  },
  {
    name: 'Nature & Seasons',
    icon: '🌍',
    emojis: [
      { emoji: '🌍', label: 'Globe' },
      { emoji: '🌞', label: 'Sun' },
      { emoji: '🌙', label: 'Moon' },
      { emoji: '⛅', label: 'Partly cloudy' },
      { emoji: '🌧️', label: 'Rain' },
      { emoji: '❄️', label: 'Snow' },
      { emoji: '🌸', label: 'Cherry blossom' },
      { emoji: '🍂', label: 'Fallen leaf' },
      { emoji: '🌊', label: 'Wave' },
      { emoji: '⛰️', label: 'Mountain' },
      { emoji: '🏖️', label: 'Beach' },
      { emoji: '🏕️', label: 'Camping' },
    ],
  },
  {
    name: 'General',
    icon: '⭐',
    emojis: [
      { emoji: '⭐', label: 'Star' },
      { emoji: '🎯', label: 'Target' },
      { emoji: '📌', label: 'Pin' },
      { emoji: '🔔', label: 'Bell' },
      { emoji: '✅', label: 'Check' },
      { emoji: '🏆', label: 'Trophy' },
      { emoji: '🔥', label: 'Fire' },
      { emoji: '⚡', label: 'Lightning' },
      { emoji: '🎉', label: 'Party' },
      { emoji: '🎊', label: 'Confetti ball' },
      { emoji: '💝', label: 'Heart gift' },
      { emoji: '🏅', label: 'Medal' },
      { emoji: '🌟', label: 'Glowing star' },
      { emoji: '💫', label: 'Dizzy star' },
      { emoji: '🚀', label: 'Rocket' },
      { emoji: '🪄', label: 'Magic wand' },
      { emoji: '♻️', label: 'Recycle' },
      { emoji: '🔒', label: 'Lock' },
      { emoji: '📍', label: 'Location pin' },
      { emoji: '🏷️', label: 'Tag' },
    ],
  },
];

/** Flat list of all emojis for quick lookup */
const ALL_EMOJIS = EMOJI_CATEGORIES.flatMap((c) => c.emojis);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface EmojiPickerProps {
  /** Currently selected emoji (Unicode character) */
  value?: string;
  /** Callback when the user picks or clears an emoji */
  onChange: (emoji: string) => void;
  /** Optional label displayed above the trigger */
  label?: string;
  /** Extra className on the outer wrapper */
  className?: string;
}

export function EmojiPicker({ value, onChange, className }: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const filtered = useMemo(() => {
    if (!search.trim()) {
      // If a category tab is active, show only that category
      if (activeCategory) {
        return EMOJI_CATEGORIES.filter((cat) => cat.name === activeCategory);
      }
      return EMOJI_CATEGORIES;
    }
    const q = search.toLowerCase();
    return EMOJI_CATEGORIES.map((cat) => ({
      ...cat,
      emojis: cat.emojis.filter(
        (e) =>
          e.label.toLowerCase().includes(q) ||
          e.emoji === q ||
          cat.name.toLowerCase().includes(q)
      ),
    })).filter((cat) => cat.emojis.length > 0);
  }, [search, activeCategory]);

  const handleSelect = (emoji: string) => {
    onChange(emoji);
    setOpen(false);
    setSearch('');
    setActiveCategory(null);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const handleCategoryClick = (catName: string) => {
    if (activeCategory === catName) {
      setActiveCategory(null);
    } else {
      setActiveCategory(catName);
      setSearch('');
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearch('');
      setActiveCategory(null);
    }
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            type="button"
            className="h-11 min-h-[44px] w-full min-w-0 justify-start overflow-hidden text-left font-normal whitespace-normal"
          >
            {value ? (
              <span className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 text-lg leading-none">{value}</span>
                <span className="min-w-0 flex-1 break-words text-sm text-muted-foreground [overflow-wrap:anywhere] leading-tight">
                  {ALL_EMOJIS.find((e) => e.emoji === value)?.label ?? 'Emoji'}
                </span>
              </span>
            ) : (
              <span className="min-w-0 flex-1 break-words text-muted-foreground [overflow-wrap:anywhere] leading-tight">Pick an emoji (optional)</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0"
          align="start"
          side="bottom"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {/* Search input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search emojis by name…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (e.target.value.trim()) setActiveCategory(null);
                }}
                className="h-8 text-sm pl-8"
                autoFocus
              />
            </div>
          </div>

          {/* Category tabs */}
          {!search.trim() && (
            <div className="flex items-center gap-0.5 px-2 py-1.5 border-b overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              <button
                type="button"
                className={cn(
                  'shrink-0 px-1.5 py-1 rounded text-xs font-medium transition-colors',
                  !activeCategory
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent text-muted-foreground'
                )}
                onClick={() => setActiveCategory(null)}
              >
                All
              </button>
              {EMOJI_CATEGORIES.map((cat) => (
                <button
                  key={cat.name}
                  type="button"
                  className={cn(
                    'shrink-0 px-1 py-1 rounded text-base transition-colors',
                    activeCategory === cat.name
                      ? 'bg-primary/10 ring-1 ring-primary'
                      : 'hover:bg-accent'
                  )}
                  title={cat.name}
                  onClick={() => handleCategoryClick(cat.name)}
                >
                  {cat.icon}
                </button>
              ))}
            </div>
          )}

          {/* Emoji grid — native scroll for reliable mousewheel & touch */}
          <div className="h-[280px] overflow-y-auto scrollbar-elegant pointer-events-auto">
            <div ref={scrollRef} className="p-2 space-y-3">
              {filtered.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-8">
                  No emoji matching &ldquo;{search}&rdquo;
                </p>
              )}
              {filtered.map((cat) => (
                <div
                  key={cat.name}
                  ref={(el) => { categoryRefs.current[cat.name] = el; }}
                >
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 px-0.5">
                    {cat.icon} {cat.name}
                  </p>
                  <div className="grid grid-cols-8 gap-1">
                    {cat.emojis.map((e) => (
                      <button
                        key={e.emoji}
                        type="button"
                        className={cn(
                          'flex items-center justify-center h-9 w-full rounded-md text-xl',
                          'hover:bg-accent hover:scale-110 transition-all duration-100',
                          value === e.emoji && 'ring-2 ring-primary bg-accent'
                        )}
                        title={e.label}
                        onClick={() => handleSelect(e.emoji)}
                      >
                        {e.emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer — selected summary */}
          {value && (
            <div className="flex items-center justify-between border-t px-3 py-1.5">
              <span className="text-xs text-muted-foreground">
                Selected: <span className="text-base">{value}</span>{' '}
                {ALL_EMOJIS.find((e) => e.emoji === value)?.label}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={handleClear}
              >
                Clear
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Clear button — shown only when an emoji is selected */}
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={handleClear}
          aria-label="Clear emoji"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export { EMOJI_CATEGORIES, ALL_EMOJIS };
